/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';

import AiManagementDeclarationEditor, {
  DeclarationEditorField,
  DeclarationEditorHandle,
} from 'components/molecules/ai_management_declaration_editor';
import { loadProposalCompanyData } from 'components/molecules/ai_management_proposal_document/load';
import type { RequirementBusy } from 'hooks/useProposalDocuments';
import type { ProposalCompanyData } from 'services/actions/ai_management_proposal_company';
import type { ProposalRequirementPatch } from 'services/actions/ai_management_proposal_documents';
import {
  DeclarationField,
  ProposalRequirement,
  fieldNeedsAction,
} from 'services/ai_management_proposal_documents';
import {
  DECLARATION_FIELDS,
  DECLARATION_TEMPLATES,
  DeclarationFieldValues,
  DeclarationTemplate,
  declarationTemplateById,
  renderDeclarationTemplateHtml,
  templateFieldIds,
} from 'services/declaration_templates';

export type DeclarationSavePayload = Pick<
  ProposalRequirementPatch,
  'campos' | 'texto_final' | 'texto_origem' | 'modelo_id'
>;

interface AiManagementProposalDeclarationBodyProps {
  requirement: ProposalRequirement;
  locked: boolean;
  busy?: RequirementBusy;
  onSave: (payload: DeclarationSavePayload) => Promise<unknown>;
  onConfirm: () => void;
  onPickFile: () => void;
  // Envio que substitui o texto: o arquivo passa a ser a declaração.
  onUploadFile?: (file: File) => Promise<unknown>;
  biddingId?: number;
  editalNumber?: string | null;
}

const SAVE_DEBOUNCE = 700;

const plural = (n: number, singular: string, varios: string) => (n === 1 ? singular : varios);

const texto = (valor: unknown) => (typeof valor === 'string' && valor.trim() ? valor.trim() : null);

// Valores que pré-preenchem o modelo: o número do edital e os dados da empresa. O cadastro não
// tem cidade nem órgão expedidor do RG; esses ficam pendentes, como os do credenciado.
const valoresDoModelo = (
  dados: ProposalCompanyData | null,
  edital: string | null | undefined,
): DeclarationFieldValues => {
  const representante = dados?.representante_legal;
  return {
    edital: texto(edital),
    razao_social: texto(dados?.razao_social),
    cnpj: texto(dados?.cnpj),
    representante_nome: texto(representante?.nome),
    representante_cargo: texto(representante?.cargo),
    representante_rg: texto(representante?.rg),
    representante_cpf: texto(representante?.cpf),
    cidade: texto(dados?.cidade),
  };
};

// Corpo da declaração: o texto com os campos, a comprovação e as saídas para a declaração sem
// texto no edital. Grava sozinho (debounce), ao sair da etapa e antes de confirmar.
const AiManagementProposalDeclarationBody: React.FC<AiManagementProposalDeclarationBodyProps> = ({
  requirement,
  locked,
  busy,
  onSave,
  onConfirm,
  onPickFile,
  onUploadFile,
  biddingId,
  editalNumber,
}) => {
  const declaration = requirement.declaration!;
  const editorRef = useRef<DeclarationEditorHandle>(null);
  const [writing, setWriting] = useState(false);
  // Escolher um modelo abre o editor de novo (outra key), já com o texto gravado. O back não
  // devolve o valor sugerido dos campos de modelo: fica guardado aqui para o "Desfazer
  // preenchimento".
  const [version, setVersion] = useState(0);
  const [suggestedById, setSuggestedById] = useState<Record<string, string | null>>({});
  const [picking, setPicking] = useState(false);
  const [substituindo, setSubstituindo] = useState(false);
  const substituirInput = useRef<HTMLInputElement>(null);
  const [applying, setApplying] = useState(false);
  const [modelError, setModelError] = useState<string | null>(null);
  const [modelId, setModelId] = useState(
    declaration.origem === 'modelo' ? declaration.modelId : null,
  );
  const [campos, setCampos] = useState<DeclarationEditorField[]>(() =>
    declaration.fields.map((f) => ({ id: f.id, valor: f.value, situacao: f.situacao })),
  );
  const [hasText, setHasText] = useState(Boolean(declaration.html.trim()));

  const pending = useRef<DeclarationSavePayload | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const flush = useCallback((): Promise<unknown> => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const payload = pending.current;
    pending.current = null;
    return payload ? onSaveRef.current(payload) : Promise.resolve();
  }, []);

  useEffect(
    () => () => {
      flush();
    },
    [flush],
  );

  const handleChange = (html: string, novos: DeclarationEditorField[]) => {
    setCampos(novos);
    setHasText(Boolean(html.replace(/<[^>]*>/g, '').trim()) || novos.length > 0);
    pending.current = {
      texto_final: html,
      campos: novos,
      ...(writing && declaration.origem !== 'livre' ? { texto_origem: 'livre' as const } : {}),
    };
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, SAVE_DEBOUNCE);
  };

  const confirmar = () => {
    flush().then(onConfirm);
  };

  // O arquivo enviado passa a ser a declaração: o texto pré-preenchido (edital ou modelo) sai.
  const substituirPorArquivo = async (file: File) => {
    if (!onUploadFile) return;
    const result = (await onUploadFile(file)) as { error?: string | null } | undefined;
    if (result?.error) return;
    if (timer.current) clearTimeout(timer.current);
    pending.current = null;
    await onSaveRef.current({ texto_final: '', campos: [], texto_origem: 'livre' });
    setSubstituindo(false);
    setWriting(false);
    setModelId(null);
    setHasText(false);
  };

  const aplicarModelo = async (template: DeclarationTemplate) => {
    setApplying(true);
    setModelError(null);
    try {
      const company = biddingId ? await loadProposalCompanyData(biddingId).catch(() => null) : null;
      const valores = valoresDoModelo(company?.dados ?? null, editalNumber);
      const html = renderDeclarationTemplateHtml(template.id, valores, moment().format('LL'));
      const fields: DeclarationField[] = templateFieldIds(template).map((id) => ({
        id,
        label: DECLARATION_FIELDS[id].rotulo,
        value: valores[id] ?? null,
        suggested: valores[id] ?? null,
        situacao: valores[id] ? 'sugerido' : 'pendente',
      }));
      const novos = fields.map((f) => ({ id: f.id, valor: f.value, situacao: f.situacao }));
      if (timer.current) clearTimeout(timer.current);
      pending.current = null;
      const result = (await onSaveRef.current({
        texto_origem: 'modelo',
        modelo_id: template.id,
        texto_final: html,
        campos: novos,
      })) as { error?: string | null } | undefined;
      if (result?.error) {
        setModelError(result.error);
        return;
      }
      setSuggestedById(Object.fromEntries(fields.map((f) => [f.id, f.suggested])));
      setVersion((v) => v + 1);
      setCampos(novos);
      setHasText(true);
      setModelId(template.id);
      setPicking(false);
    } catch (e) {
      setModelError('Não foi possível usar o modelo. Tente de novo.');
    } finally {
      setApplying(false);
    }
  };

  // O modelo sugerido pelo Analista vem primeiro, marcado; sem sugestão, a ordem de sempre.
  // Declaração sem texto no edital com modelo sugerido já abre com ele pré-preenchido; o
  // cliente troca em "Trocar modelo". Aplica uma vez só: depois ela já tem texto.
  const sugeridoAplicado = useRef(false);
  const aplicarModeloRef = useRef(aplicarModelo);
  aplicarModeloRef.current = aplicarModelo;
  useEffect(() => {
    const sugerido = declarationTemplateById(declaration.suggestedModel);
    if (
      sugeridoAplicado.current ||
      !sugerido ||
      locked ||
      hasText ||
      requirement.files.length > 0
    ) {
      return;
    }
    sugeridoAplicado.current = true;
    aplicarModeloRef.current(sugerido);
  }, [declaration.suggestedModel, locked, hasText, requirement.files.length]);

  const renderModelPicker = (trocando: boolean) => {
    const sugerido = declarationTemplateById(declaration.suggestedModel);
    const modelos = sugerido
      ? [sugerido, ...DECLARATION_TEMPLATES.filter((t) => t.id !== sugerido.id)]
      : DECLARATION_TEMPLATES;
    return (
      <div className="card p-3 mb-2">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <strong>Escolha o modelo de declaração</strong>
          <button
            type="button"
            className="btn btn-sm btn-link p-0"
            disabled={applying}
            onClick={() => setPicking(false)}
          >
            Cancelar
          </button>
        </div>
        {trocando && <p className="text-danger small mb-2">O texto atual será substituído.</p>}
        <ul className="list-unstyled mb-0">
          {modelos.map((modelo) => (
            <li key={modelo.id} className="mb-1">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary text-left"
                disabled={applying}
                onClick={() => aplicarModelo(modelo)}
              >
                {modelo.label}
              </button>
              {sugerido?.id === modelo.id && (
                <span className="badge badge-info ml-2">Sugerido</span>
              )}
            </li>
          ))}
        </ul>
        {applying && <small className="text-muted d-block mt-2">Preenchendo o modelo…</small>}
        {modelError && <div className="text-danger small mt-2">{modelError}</div>}
      </div>
    );
  };

  const working = Boolean(busy);
  const showEditor = hasText || writing;
  const hasFiles = requirement.files.length > 0;

  if (!showEditor) {
    return (
      <div className="mb-2">
        <p className="text-muted mb-2">
          {hasFiles
            ? 'Declaração enviada em arquivo.'
            : 'O edital pede esta declaração, mas não traz o texto dela.'}
        </p>
        {!locked && (
          <div className="d-flex flex-wrap align-items-center">
            <button
              type="button"
              className="btn btn-primary mr-2 mb-2"
              disabled={working}
              onClick={() => setWriting(true)}
            >
              Escrever a declaração
            </button>
            <button
              type="button"
              className={`btn ${
                hasFiles ? 'btn-outline-primary' : 'btn-outline-secondary'
              } mr-2 mb-2`}
              disabled={working}
              onClick={onPickFile}
            >
              {busy === 'upload' && 'Enviando…'}
              {busy !== 'upload' && (hasFiles ? 'Enviar outro arquivo' : 'Enviar documento')}
            </button>
            <button
              type="button"
              className="btn btn-outline-primary mr-2 mb-2"
              disabled={working || applying}
              onClick={() => setPicking(true)}
            >
              Usar modelo de declaração
            </button>
            {hasFiles && (
              <button
                type="button"
                className="btn btn-primary mr-2 mb-2"
                disabled={working}
                onClick={onConfirm}
              >
                {busy === 'confirm' ? 'Confirmando…' : 'Confirmar documento'}
              </button>
            )}
          </div>
        )}
        {!locked && picking && renderModelPicker(false)}
      </div>
    );
  }

  const aTratar = campos.filter(
    (c) => c.situacao === 'sugerido' || c.situacao === 'pendente',
  ).length;
  const recusadosVazios = campos.filter(
    (c) => c.situacao === 'recusado' && fieldNeedsAction({ situacao: c.situacao, value: c.valor }),
  ).length;
  const faltaComprovacao = declaration.needsProof && !hasFiles;
  let motivo: string | null = null;
  if (aTratar > 0) {
    motivo = `Aceite ou recuse ${aTratar} ${plural(aTratar, 'campo', 'campos')} para confirmar`;
  } else if (recusadosVazios > 0) {
    motivo = `Preencha ${recusadosVazios} ${plural(
      recusadosVazios,
      'campo recusado',
      'campos recusados',
    )} para confirmar`;
  } else if (faltaComprovacao) {
    motivo = 'Envie o documento de comprovação para confirmar';
  }
  const temSugeridos = campos.some((c) => c.situacao === 'sugerido');

  return (
    <div className="mb-2">
      <h3 style={{ fontSize: 15, fontWeight: 600, color: '#21354a', marginBottom: 4 }}>
        {declaration.origem === 'edital' ? 'Declaração extraída do edital' : 'Texto da declaração'}
      </h3>
      {declaration.origem === 'edital' && (
        <p className="mb-2" style={{ color: '#495057' }}>
          O texto está como no edital. O Analista preencheu os campos com os dados da empresa.
        </p>
      )}

      {modelId && (
        <div className="d-flex align-items-center mb-2">
          <span className="text-muted">
            Modelo: {declarationTemplateById(modelId)?.label ?? modelId}
          </span>
          {!locked && !picking && (
            <button
              type="button"
              className="btn btn-sm btn-link ml-2 p-0"
              disabled={working || applying}
              onClick={() => setPicking(true)}
            >
              Trocar modelo
            </button>
          )}
        </div>
      )}
      {!locked && picking && renderModelPicker(true)}

      <AiManagementDeclarationEditor
        key={`${requirement.id}-${locked ? 'leitura' : 'edicao'}-${version}`}
        ref={editorRef}
        initialHtml={declaration.html}
        fields={declaration.fields.map((f) => ({
          ...f,
          suggested: f.suggested ?? suggestedById[f.id] ?? null,
        }))}
        readOnly={locked}
        onChange={handleChange}
        showFillActions={campos.length > 0}
        canAcceptFill={temSugeridos}
      />

      {declaration.needsProof && (
        <div className="mt-2">
          <p className="mb-1">
            <strong>Comprovação:</strong> {declaration.proofLabel ?? 'documento'}
          </p>
        </div>
      )}

      {!locked && (
        <div className="d-flex flex-wrap align-items-center mt-2">
          {declaration.needsProof ? (
            <button
              type="button"
              className={`btn ${!hasFiles ? 'btn-primary' : 'btn-outline-secondary'} mr-2 mb-2`}
              disabled={working}
              onClick={onPickFile}
            >
              {busy === 'upload' && 'Enviando…'}
              {busy !== 'upload' &&
                `Enviar documento${declaration.proofLabel ? ` — ${declaration.proofLabel}` : ''}`}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-outline-secondary mr-2 mb-2"
              disabled={working || substituindo}
              onClick={() => setSubstituindo(true)}
            >
              {busy === 'upload' ? 'Enviando…' : 'Enviar declaração em arquivo'}
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary mr-2 mb-2"
            disabled={working || !!motivo}
            onClick={confirmar}
          >
            {busy === 'confirm' ? 'Confirmando…' : 'Confirmar declaração'}
          </button>
          {motivo && <small className="text-muted mb-2">{motivo}</small>}
        </div>
      )}

      {!locked && substituindo && (
        <div className="alert alert-warning d-flex align-items-center flex-wrap mt-2 mb-0">
          <span className="mr-3">O texto da declaração será substituído pelo arquivo enviado.</span>
          <input
            ref={substituirInput}
            type="file"
            accept=".pdf,.docx"
            className="d-none"
            onChange={(event) => {
              const file = event.target.files?.[0];
              // eslint-disable-next-line no-param-reassign
              event.target.value = '';
              if (file) substituirPorArquivo(file);
            }}
          />
          <button
            type="button"
            className="btn btn-sm btn-primary mr-2"
            disabled={working}
            onClick={() => substituirInput.current?.click()}
          >
            {busy === 'upload' ? 'Enviando…' : 'Escolher arquivo'}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-link"
            onClick={() => setSubstituindo(false)}
          >
            Cancelar
          </button>
        </div>
      )}
    </div>
  );
};

export default AiManagementProposalDeclarationBody;
